import React, { FC, useState, useEffect, useMemo } from 'react';
import { ReactComponent as ReassignRiderIcon } from 'assets/icons/reassign-rider-modal-icon.svg';
import { toast } from 'react-toastify';
import './styles.scss';
import {
  Button,
  ToastMessage,
  type MultiselectItem,
  BaseModal,
  Dropdown,
  DropdownList,
  DropdownItem,
  Input
} from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { RiderType } from 'models/rider.model';
import { ArrowDownIcon } from 'cokitchen-icons';
import { getUserName, handleApiError } from 'utils/functions';

interface ReassignRiderModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess?: (selectedRider: RiderType) => void;
  order: OrderType;
}

interface ReassignRiderFooterProps {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ReassignRiderFooter: FC<ReassignRiderFooterProps> = ({ onCancel, onConfirm, loading }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const ReassignRiderModal: FC<ReassignRiderModalProps> = ({ show, onClose, order, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [riders, setRiders] = useState<MultiselectItem[]>([]);
  const [selectedRider, setSelectedRider] = useState({} as RiderType);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const reassignRider = async () => {
    try {
      await apiService.post('logistics/order/rider/change', {
        data: {
          order_id: order.id,
          rider_id: selectedRider.id
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Changed rider successfully' />);
      onClose();
      onSuccess?.(selectedRider);
    } catch (err) {
      handleApiError(err);
    }
  };
  const loadRiders = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('logistics/riders/active', {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      const data = res.data.riders;
      if (!data) return;

      const otherRiders = data.filter((rider: RiderType) => rider.id !== order.rider.id);
      setRiders([order.rider, ...otherRiders]);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (show) {
      loadRiders();
      setSelectedRider(order.rider);
    }
  }, [show]);

  useEffect(() => {
    setShowDropdown(false);
  }, [selectedRider]);

  const filteredRiders = useMemo(() => {
    const filtered = riders.filter((rider) =>
      getUserName(rider).toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered;
  }, [riders, searchValue]);

  return (
    <BaseModal
      isOpen={show}
      title={'Re-assign Rider for #' + order.order_code}
      description='Change rider assigned to deliver this order'
      titleIcon={<ReassignRiderIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={
        <ReassignRiderFooter loading={loading} onCancel={onClose} onConfirm={reassignRider} />
      }>
      <Dropdown
        visible={showDropdown}
        onClickOutside={() => setShowDropdown(false)}
        content={
          <div
            onClick={() => setShowDropdown(!showDropdown)}
            className='px-3 py-4 flex items-center justify-between text-sm font-medium rounded-md bg-white border border-gray-6'>
            <span>{getUserName(selectedRider)}</span>
            <ArrowDownIcon className='w-5 h-5' />
          </div>
        }>
        <DropdownList className='min-w-[300px]'>
          <div className='sticky pl-1 top-0 h-[45px]'>
            <Input
              className='text-sm'
              placeholder='Search rider'
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              inputClass='h-[44px] text-sm'
            />
          </div>

          {loading ? (
            <DropdownItem isLabel>Loading...</DropdownItem>
          ) : filteredRiders.length === 0 ? (
            <DropdownItem className='text-gray-500 mt-2'>
              There are no riders like {searchValue}
            </DropdownItem>
          ) : (
            filteredRiders.map((rider) => (
              <DropdownItem key={rider.id} onClick={() => setSelectedRider(rider)}>
                <Input
                  className='text-sm'
                  type='radio'
                  checked={selectedRider.id === rider.id}
                  readOnly
                  label={getUserName(rider)}
                />
              </DropdownItem>
            ))
          )}
        </DropdownList>
      </Dropdown>
    </BaseModal>
  );
};

export default ReassignRiderModal;
